import React from 'react';
import { useQuery } from '@apollo/client';
import { sendProductWidgetCtaClickEvent } from 'snowplow';

import { Product } from 'ggApp/types/graphql';
import CircleLoader from 'ggApp/shared/components/CircleLoader';
import { productAdapter } from 'ggApp/shared/components/widgets/utils/graphqlWidgetProductMapper';
import { ActiveStore } from 'ggApp/modules/store/selectors';
import DisplayIf from 'ggApp/shared/components/viewport';
import Carousel from 'ggApp/shared/components/Carousel';
import { PureTranslator } from 'ggApp/modules/translator/components';
import GetCountdownTimerById from 'ggApp/shared/screens/GPage/queries/GetCountdownTimerById.graphql';
import { getOrderFromCookiesBasedStore } from 'ggApp/shared/context/Order/cookies';

import { Wrap, TitleWrap, Title, Countdown, Cta, Grid, ProductCard } from './styles';

export type CountdownWidgetProps = {
    className?: string;
    widgetData: {
        name: string;
        products: Product[];
        properties: {
            countdownId?: string;
        };
    };
    title?: string;
    label?: string;
    url?: string;
};

export type EnhancedProps = {
    favoriteList: number[];
    activeStore: ActiveStore;
    userID?: number;
};

const CountdownWidget = ({
    className,
    widgetData,
    title,
    label,
    url,
    favoriteList,
    activeStore: {
        default_currency: defaultCurrency,
        activeLanguage,
        code: storeCode,
        store_id: storeId,
    },
    userID,
}: CountdownWidgetProps & EnhancedProps): JSX.Element | null => {
    const isProductInFavoriteList = (productId: number) => {
        return favoriteList.includes(productId);
    };

    const productList =
        widgetData?.products &&
        widgetData.products.map((product: Product) =>
            productAdapter({
                product,
                defaultCurrency,
                activeLanguage,
                favorite: isProductInFavoriteList(parseInt(product.id, 10)),
            }),
        );

    const countdownId = widgetData?.properties?.countdownId;
    const { data, loading } = useQuery(GetCountdownTimerById, {
        variables: { id: countdownId },
        skip: !countdownId,
    });
    const countdownData = data?.findCountdownTimerById;

    const handleCtaClick = () => {
        sendProductWidgetCtaClickEvent({
            storeId,
            userID,
            orderID: getOrderFromCookiesBasedStore(storeCode),
            widgetName: countdownData?.name,
        });
    };

    if (loading) {
        return <CircleLoader className={className} />;
    }

    if (!countdownData?.isActive) return null;

    return (
        <Wrap className={className}>
            <TitleWrap>
                <Title>
                    <PureTranslator tk={title} />
                </Title>
                {countdownData ? <Countdown countdownById={countdownData} /> : null}
            </TitleWrap>
            <DisplayIf tablet desktop largeDesktop widescreen>
                <Grid>
                    {productList.map((product: any) => (
                        <ProductCard
                            key={`${product.slug}`}
                            product={product}
                            cardType="vertical"
                        />
                    ))}
                </Grid>
            </DisplayIf>
            <DisplayIf mobile>
                <Carousel products={productList} cardWidth={280} />
            </DisplayIf>
            {!!url && (
                <Cta
                    onClick={handleCtaClick}
                    target="_self"
                    linkTo={url}
                    text={<PureTranslator tk={label} />}
                />
            )}
        </Wrap>
    );
};

export default CountdownWidget;
